import React, { useEffect, useState } from 'react';

import { Map, useApiIsLoaded, useMap } from '@vis.gl/react-google-maps';
import get from 'lodash/get';
import PropTypes from 'prop-types';

import {
    SEGMENT_COLOR,
    SEGMENT_STROKE_COLOR,
    SOLAR_MODULE_COLOR,
    SOLAR_MODULE_STROKE_COLOR,
} from 'common/components/maps/constants';
import { DEFAULT_LAT, DEFAULT_LNG } from 'common/constants';
import { arraytoDictionary } from 'common/utils/helpers';

import { DELTA, mapStyles } from './constants';
import { mapValueUseEffect } from './helpers';
import WrapperMap from './WrapperMap';

const SegmentsMap = ({
    center = { lat: DEFAULT_LAT, lng: DEFAULT_LNG },
    clickable = true,
    fullscreenControl = false,
    handleCenterChanged,
    handleZoomChanged,
    height,
    initCenter,
    projectLocation,
    scaleControl = true,
    segmentFillColor = SEGMENT_COLOR,
    segmentStrokeColor = SEGMENT_STROKE_COLOR,
    segments = [],
    showLabels = true,
    showProjectMarker = true,
    solarModuleFillColor = SOLAR_MODULE_COLOR,
    solarModuleStrokeColor = SOLAR_MODULE_STROKE_COLOR,
    width,
    zoom = 18,
    zoomControl = false,
}) => {
    const [google, setGoogle] = useState(null);
    const apiIsLoaded = useApiIsLoaded();
    const map = useMap();

    const [shapes, setShapes] = useState({});
    const initialCenter = {
        lat: get(initCenter, 'latitude', center.lat),
        lng: get(initCenter, 'longitude', center.lng),
    };
    const projectMarkerCenter = {
        lat: get(projectLocation, 'latitude', center.lat),
        lng: get(projectLocation, 'longitude', center.lng),
    };

    useEffect(() => {
        if (!apiIsLoaded) return;
        setGoogle(window.google);
    }, [apiIsLoaded]);

    useEffect(() => {
        if (!map && !google) return;
        map.setOptions({
            fullscreenControl: false,
            gestureHandling: 'none',
            panControl: false,
            restriction: {
                latLngBounds: {
                    east: center.lng + DELTA,
                    north: center.lat + DELTA,
                    south: center.lat - DELTA,
                    west: center.lng - DELTA,
                },
                strictBounds: true,
            },
            scaleControl: false,
            scrollwheel: false,
            zoomControl: false,
        });
    }, [map, google]);

    useEffect(() => {
        if (!map || !google) return;
        map.setOptions({ zoomControl });
    }, [zoomControl, map, google]);

    useEffect(() => {
        if (!map || !google) return;
        map.setOptions({ scaleControl });
    }, [scaleControl, map, google]);

    useEffect(() => {
        Object.keys(shapes).forEach((key) => {
            shapes[key].setLabelsVisibility(showLabels);
        });
    }, [showLabels, shapes]);

    useEffect(() => {
        if (!map || !google || !projectMarker) return;
        if (showProjectMarker) projectMarker.map = map;
        else projectMarker.map = null;
    }, [showProjectMarker, map, google, projectMarker]);

    useEffect(() => {
        Object.keys(shapes).forEach((key) => {
            shapes[key].setOptions({
                fillColor: segmentFillColor,
            });
        });
    }, [segmentFillColor, shapes]);

    useEffect(() => {
        Object.keys(shapes).forEach((key) => {
            shapes[key].setOptions({
                strokeColor: segmentStrokeColor,
            });
        });
    }, [segmentStrokeColor, shapes]);

    useEffect(() => {
        Object.keys(shapes).forEach((key) => {
            shapes[key].setSolarModulesFillColor(solarModuleFillColor);
        });
    }, [solarModuleFillColor, shapes]);

    useEffect(() => {
        Object.keys(shapes).forEach((key) => {
            shapes[key].setSolarModulesStrokeColor(solarModuleStrokeColor);
        });
    }, [solarModuleStrokeColor, shapes]);

    const [projectMarker, setProjectMarker] = useState(null);

    useEffect(() => {
        if (!google) return;

        const marker = new google.maps.marker.AdvancedMarkerElement({
            position: new google.maps.LatLng(
                projectMarkerCenter.lat,
                projectMarkerCenter.lng,
            ),
        });

        setProjectMarker(marker);

        return () => {
            if (marker) {
                marker.map = null;
            }
        };
    }, [google, projectMarkerCenter.lat, projectMarkerCenter.lng]);

    useEffect(
        mapValueUseEffect({
            google,
            handleCenterChanged,
            handleZoomChanged,
            mapValue: map,
            projectMarker,
            scaleControl,
            segmentsDictionary: arraytoDictionary(segments, 'id'),
            setShapes,
            showLabels,
            showProjectMarker,
            solarModuleFillColor,
            solarModuleStrokeColor,
            zoomControl,
        }),
        [map, google],
    );

    return (
        <WrapperMap
            className={!clickable && 'unclickable'}
            height={height}
            width={width}
        >
            <Map
                fullscreenControl={fullscreenControl}
                defaultCenter={initialCenter}
                mapTypeId="satellite"
                mapTypeControl={false}
                mapId={import.meta.env.VITE_GOOGLE_MAPS_DEFAULT_ID}
                rotateControl={false}
                scaleControl={scaleControl}
                streetViewControl={false}
                styles={mapStyles}
                defaultZoom={zoom}
            />
        </WrapperMap>
    );
};

SegmentsMap.propTypes = {
    center: PropTypes.object,
    clickable: PropTypes.bool,
    fullscreenControl: PropTypes.bool,
    handleCenterChanged: PropTypes.func,
    handleZoomChanged: PropTypes.func,
    height: PropTypes.string,
    initCenter: PropTypes.object,
    projectLocation: PropTypes.object,
    scaleControl: PropTypes.bool,
    segmentFillColor: PropTypes.string,
    segments: PropTypes.array,
    segmentStrokeColor: PropTypes.string,
    showLabels: PropTypes.bool,
    showProjectMarker: PropTypes.bool,
    solarModuleFillColor: PropTypes.string,
    solarModuleStrokeColor: PropTypes.string,
    width: PropTypes.string,
    zoom: PropTypes.number,
    zoomControl: PropTypes.bool,
};

export default SegmentsMap;
